import StoryPageDropZone from '@src/components/Dashboard/user-story/StoryPageDropZone';
import { useDefaultOrgRedirect } from '@src/hooks/useDefaultOrgRedirect';
import useVideoCreatorDataInitialization from '@src/hooks/useVideoCreatorDataInitialization';
import { useDatoPerson, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Studio } from '@src/types.ts/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { PaymentFailedBanner } from './PaymentFailedBanner';
import { QuickActions } from './QuickActions';
import { RecentContent } from './RecentContent';
import { RecentStories } from './RecentStories';

export const HomePage = observer(() => {
  // Open the first available organization when none is in the URL yet.
  useDefaultOrgRedirect();

  // Initialize data stores (same as Dashboard)
  useVideoCreatorDataInitialization();

  const userIdentity = useUserIdentity();
  const videoCreator = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const album = datoPerson.selectedOrganization || videoCreator.organization;
  const { enableHomeQuickActions, homeActionsEnableAddStoryButton } =
    useFlagsCombination(userIdentity.currentRole);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Same landing as the Add Story quick action: the Story Manager, on the
  // new story's row.
  const openStoryManager = ({ storyId }: { storyId: string }) => {
    const params = new URLSearchParams(searchParams);
    params.set('storyId', storyId);
    navigate(`/${Studio.dashboard}?${params.toString()}`);
  };

  return (
    <Container data-testid="home-page">
      {album?.paymentFailed && <PaymentFailedBanner />}
      <WelcomeSection>
        <WelcomeTitle>Welcome to Arbor!</WelcomeTitle>
        <WelcomeSubtitle>
          Pick up where you left off, or create something new.
        </WelcomeSubtitle>
      </WelcomeSection>

      {enableHomeQuickActions && <QuickActions />}

      <RecentStories />

      <RecentContent />

      <StoryPageDropZone
        enabled={homeActionsEnableAddStoryButton}
        entryPoint="home-drop"
        onSave={openStoryManager}
      />
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 44px 60px;
  min-height: 100vh;
  background-color: var(--background);
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  max-width: 100vw;

  @media (max-width: 1200px) {
    padding: 24px 40px;
  }

  @media (max-width: 900px) {
    padding: 24px 24px;
  }

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const WelcomeTitle = styled.h1`
  font-family: 'Georgia', serif;
  font-size: 48px;
  font-weight: var(--font-weight-regular);
  font-style: normal;
  color: var(--foreground);
  margin: 0 0 12px 0;

  @media (max-width: 600px) {
    font-size: 36px;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 16px;
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  margin: 0;
`;
